import type { Role } from '@/features/auth/roles';

/**
 * Help (MIG-04): EN/BM guides from the local Admin v2 blueprint, adjusted to
 * the implemented flows. Do not promise connected providers or face unlock.
 */
export interface HelpGuide {
  id: string;
  title: string;
  minutes: number;
  roles: Role[] | 'all';
  titleBm: string;
  steps: string[];
  stepsBm: string[];
}

export const HELP_GUIDES: HelpGuide[] = [
  {
    id: 'notify-dengue',
    title: 'Notify a dengue case',
    minutes: 2,
    roles: ['doctor'],
    titleBm: 'Memaklumkan kes denggi',
    steps: [
      'Code the diagnosis A90 or A91: a notification task opens in Safety & public health when you close the visit.',
      'Phone the District Health Office first.',
      'Submit on CDCIS e-Notification and type its number into the task. Nothing is sent from the clinic system.',
    ],
    stepsBm: [
      'Kodkan diagnosis A90 atau A91: tugasan pemberitahuan dibuka dalam Keselamatan & kesihatan awam apabila lawatan ditutup.',
      'Telefon Pejabat Kesihatan Daerah dahulu.',
      'Hantar melalui CDCIS e-Notification dan masukkan nombornya ke dalam tugasan. Tiada apa-apa dihantar dari sistem klinik.',
    ],
  },
  {
    id: 'walk-in-passport',
    title: 'Register a walk-in with a passport',
    minutes: 2,
    roles: ['front_desk'],
    titleBm: 'Daftar pesakit tanpa temu janji dengan pasport',
    steps: [
      'Open Register patient.',
      'Choose Passport, enter the passport number and nationality.',
      'Record the patient’s consents, then save the registration.',
      'Add the patient to the walk-in queue. Outbound messages are simulated until a provider is connected.',
    ],
    stepsBm: [
      'Buka Daftar pesakit.',
      'Pilih Pasport, masukkan nombor pasport dan kewarganegaraan.',
      'Rekod persetujuan pesakit, kemudian simpan pendaftaran.',
      'Tambah pesakit ke giliran. Mesej keluar disimulasikan sehingga penyedia disambungkan.',
    ],
  },
  {
    id: 'close-cash-drawer',
    title: 'Close the cash drawer at the end of a shift',
    minutes: 3,
    roles: ['front_desk', 'finance'],
    titleBm: 'Tutup laci wang di akhir syif',
    steps: [
      'Open Cash drawer and choose Count and close shift.',
      'Count each note and the coins.',
      'Ask a second person to check, then select their name.',
      'Record the reason for any difference. The API applies the manager-review rule.',
    ],
    stepsBm: [
      'Buka Laci wang dan pilih Kira dan tutup syif.',
      'Kira setiap not dan syiling.',
      'Minta orang kedua menyemak, kemudian pilih nama mereka.',
      'Rekod sebab bagi sebarang perbezaan. API menggunakan peraturan semakan pengurus.',
    ],
  },
  {
    id: 'refund-void-discount',
    title: 'Refund, void or discount a bill',
    minutes: 2,
    roles: ['front_desk', 'clinic_manager'],
    titleBm: 'Pulangan wang, batal atau diskaun bil',
    steps: [
      'Open the bill.',
      'Choose Request refund, Void or Discount and give the reason.',
      'A manager decides the request on Approvals. Check the recorded result on the bill.',
    ],
    stepsBm: [
      'Buka bil.',
      'Pilih Mohon pulangan wang, Batal atau Diskaun dan beri sebab.',
      'Pengurus membuat keputusan di Kelulusan. Semak keputusan yang direkodkan pada bil.',
    ],
  },
  {
    id: 'screen-locked',
    title: 'The screen locked itself',
    minutes: 1,
    roles: 'all',
    titleBm: 'Skrin terkunci sendiri',
    steps: [
      'Screens lock after the configured idle period to protect patient data.',
      'Unlock with your PIN. If you cannot unlock, sign out and sign in again.',
    ],
    stepsBm: [
      'Skrin terkunci selepas tempoh tidak aktif yang ditetapkan untuk melindungi data pesakit.',
      'Buka dengan PIN anda. Jika tidak dapat membuka, log keluar dan log masuk semula.',
    ],
  },
  {
    id: 'phone-enquiry',
    title: 'Someone phones asking about a patient',
    minutes: 1,
    roles: ['front_desk'],
    titleBm: 'Seseorang menelefon bertanya tentang pesakit',
    steps: [
      'Do not share patient information yet.',
      'Open Patients and choose Someone is asking about a patient.',
      'Complete the identity and authority checks. Record only what the check permits; the decision is logged.',
    ],
    stepsBm: [
      'Jangan kongsi maklumat pesakit dahulu.',
      'Buka Pesakit dan pilih Seseorang bertanya tentang pesakit.',
      'Lengkapkan semakan identiti dan kuasa. Rekod hanya apa yang dibenarkan; keputusan direkodkan.',
    ],
  },
  {
    id: 'prescribe',
    title: 'Prescribe safely',
    titleBm: 'Menulis preskripsi dengan selamat',
    minutes: 3,
    roles: ['doctor'],
    steps: [
      'Open the consultation and enter the medicine, dose, frequency and duration.',
      'Read the recorded warnings before signing. A blocking warning must be resolved.',
      'Sign only when the prescription is ready for the dispensary.',
    ],
    stepsBm: [
      'Buka konsultasi dan masukkan ubat, dos, kekerapan dan tempoh.',
      'Baca amaran sebelum menandatangani. Amaran yang menyekat mesti diselesaikan.',
      'Tandatangan hanya apabila preskripsi sedia untuk dispensari.',
    ],
  },
  {
    id: 'dispense',
    title: 'Dispense and counsel',
    titleBm: 'Mendispens dan memberi kaunseling',
    minutes: 3,
    roles: ['dispenser'],
    steps: [
      'Open Prescriptions and select the signed prescription.',
      'Complete the required checks and counselling in the dispensing drawer.',
      'Record hand-over only after the checks are complete. Open Labels for the recorded dispensing labels.',
    ],
    stepsBm: [
      'Buka Preskripsi dan pilih preskripsi yang ditandatangani.',
      'Lengkapkan semakan dan kaunseling yang diperlukan.',
      'Rekod penyerahan selepas semakan lengkap. Buka Label untuk label pendispensan yang direkodkan.',
    ],
  },
  {
    id: 'cycle-count',
    title: 'Weekly cycle count',
    titleBm: 'Kiraan stok mingguan',
    minutes: 3,
    roles: ['dispenser', 'clinic_manager'],
    steps: [
      'Open Stock & expiry, then Counts.',
      'Start a count for the selected branch and record the physical quantities.',
      'Give a reason for differences and complete any required second check.',
    ],
    stepsBm: [
      'Buka Stok & tarikh luput, kemudian Kiraan.',
      'Mulakan kiraan bagi cawangan yang dipilih dan rekod kuantiti fizikal.',
      'Beri sebab bagi perbezaan dan lengkapkan semakan kedua jika diperlukan.',
    ],
  },
  {
    id: 'navigation-state',
    title: 'Refresh or go back without losing your place',
    titleBm: 'Muat semula atau kembali tanpa kehilangan tempat',
    minutes: 1,
    roles: 'all',
    steps: [
      'Tabs, open drawers and filters are kept in the page address.',
      'Refresh the page to restore that view, or use Browser Back to return through recent in-app views.',
      'If an API action fails, note the red error code and reference, then choose Try again.',
    ],
    stepsBm: [
      'Tab, laci terbuka dan penapis disimpan dalam alamat halaman.',
      'Muat semula halaman untuk memulihkan paparan itu, atau gunakan Kembali pelayar untuk melalui paparan aplikasi yang terkini.',
      'Jika tindakan API gagal, catat kod ralat merah dan rujukan, kemudian pilih Cuba lagi.',
    ],
  },
  {
    id: 'clock-attendance',
    title: 'Clock in, clock out and review attendance',
    titleBm: 'Rekod masuk, keluar dan semak kehadiran',
    minutes: 2,
    roles: 'all',
    steps: [
      'Use the attendance control in the top bar and confirm the working branch.',
      'Give a reason when the system asks about a late arrival or early departure.',
      'Open My attendance to review the recorded shift. Managers resolve missing clock-outs from Attendance.',
    ],
    stepsBm: [
      'Gunakan kawalan kehadiran di bar atas dan sahkan cawangan kerja.',
      'Beri sebab apabila sistem bertanya tentang ketibaan lewat atau keluar awal.',
      'Buka Kehadiran saya untuk menyemak syif. Pengurus menyelesaikan rekod keluar yang hilang melalui Kehadiran.',
    ],
  },
  {
    id: 'manage-doctor-schedules',
    title: 'Manage doctor schedules and requests',
    titleBm: 'Urus jadual doktor dan permohonan',
    minutes: 3,
    roles: ['clinic_manager', 'super_admin'],
    steps: [
      'Open Doctor schedules and add the weekly hours or a date override for the correct branch.',
      'Open the Requests tab to approve or decline a doctor’s change request.',
      'Confirm the doctor can see the resulting read-only schedule in My schedule.',
    ],
    stepsBm: [
      'Buka Jadual doktor dan tambah waktu mingguan atau perubahan tarikh bagi cawangan yang betul.',
      'Buka tab Permohonan untuk meluluskan atau menolak permohonan perubahan doktor.',
      'Sahkan doktor dapat melihat jadual baca sahaja yang terhasil dalam Jadual saya.',
    ],
  },
  {
    id: 'locum-contract-and-pay',
    title: 'Review a locum contract and weekly pay',
    titleBm: 'Semak kontrak locum dan bayaran mingguan',
    minutes: 2,
    roles: ['locum'],
    steps: [
      'Open My contract to confirm the branch, contract dates and agreed rate.',
      'Use the attendance control for every locum shift; missing clock-outs must be corrected before approval.',
      'Open My locum pay to review the weekly calculation and payment status.',
    ],
    stepsBm: [
      'Buka Kontrak saya untuk mengesahkan cawangan, tarikh kontrak dan kadar yang dipersetujui.',
      'Gunakan kawalan kehadiran bagi setiap syif locum; rekod keluar yang hilang mesti dibetulkan sebelum kelulusan.',
      'Buka Bayaran locum saya untuk menyemak pengiraan mingguan dan status bayaran.',
    ],
  },
  {
    id: 'locum-pay-approval-and-payment',
    title: 'Approve and pay a locum week',
    titleBm: 'Lulus dan bayar minggu locum',
    minutes: 2,
    roles: ['clinic_manager', 'finance'],
    steps: [
      'Clinic Manager: open Locum pay, review the weekly calculation and resolve any linked attendance correction before approval.',
      'Clinic Manager: approve the submitted week. The approved payout is then ready for Finance.',
      'Finance: open Locum payouts, process the approved week and record the bank payment reference to mark it paid.',
    ],
    stepsBm: [
      'Pengurus klinik: buka Bayaran locum, semak pengiraan mingguan dan selesaikan pembetulan kehadiran yang berkaitan sebelum meluluskan.',
      'Pengurus klinik: luluskan minggu yang dihantar. Bayaran yang diluluskan kemudian sedia untuk Kewangan.',
      'Kewangan: buka Bayaran locum, proses minggu yang diluluskan dan rekod rujukan bayaran bank untuk menandakan ia telah dibayar.',
    ],
  },
  {
    id: 'panel-limits-and-excess',
    title: 'Check panel limits and collect patient excess',
    titleBm: 'Semak had panel dan kutip lebihan pesakit',
    minutes: 3,
    roles: ['front_desk', 'finance', 'clinic_manager'],
    steps: [
      'Review the patient’s panel balance and the configured per-visit and annual limits.',
      'Confirm a near-limit warning before continuing.',
      'When the limit is exceeded, verify the panel and patient split before collecting the patient excess.',
    ],
    stepsBm: [
      'Semak baki panel pesakit serta had setiap lawatan dan tahunan yang dikonfigurasi.',
      'Sahkan amaran hampir had sebelum meneruskan.',
      'Apabila had dilebihi, semak pembahagian panel dan pesakit sebelum mengutip lebihan pesakit.',
    ],
  },
  {
    id: 'panel-receivables',
    title: 'Follow a panel receivable to closure',
    titleBm: 'Urus penerimaan panel sehingga selesai',
    minutes: 4,
    roles: ['finance', 'clinic_manager'],
    steps: [
      'Open Panel receivables and inspect the batch patient detail.',
      'Move through preparation, sending, remittance matching and variance resolution using the available actions.',
      'Record follow-ups and close only when the remittance variance is zero.',
    ],
    stepsBm: [
      'Buka Penerimaan panel dan semak butiran pesakit dalam kelompok.',
      'Ikuti penyediaan, penghantaran, padanan kiriman wang dan penyelesaian perbezaan menggunakan tindakan yang tersedia.',
      'Rekod tindakan susulan dan tutup hanya apabila perbezaan kiriman wang ialah sifar.',
    ],
  },
  {
    id: 'finance-by-patient',
    title: 'Review panel history for a patient',
    titleBm: 'Semak sejarah panel bagi pesakit',
    minutes: 2,
    roles: ['finance'],
    steps: [
      'Open By patient and search by the patient name or IC.',
      'Open the matching result, then choose View ledger to review the claims, panel usage and balances returned for that patient.',
      'Choose Export claims history to download the CSV for the current search.',
    ],
    stepsBm: [
      'Buka Mengikut pesakit dan cari mengikut nama pesakit atau IC.',
      'Buka hasil yang sepadan, kemudian pilih Lihat lejar untuk menyemak tuntutan, penggunaan panel dan baki yang dipulangkan bagi pesakit tersebut.',
      'Pilih Eksport sejarah tuntutan untuk memuat turun CSV bagi carian semasa.',
    ],
  },
  {
    id: 'consult-and-refer',
    title: 'Call, consult, sign and refer a patient',
    titleBm: 'Panggil, konsultasi, tandatangan dan rujuk pesakit',
    minutes: 4,
    roles: ['doctor', 'locum'],
    steps: [
      'Use My patients filters, then call the next eligible patient into an available room.',
      'Complete the full-page consultation and resolve every sign-and-close checklist item.',
      'Create the referral letter, review its side-by-side preview, then sign or print it when ready.',
    ],
    stepsBm: [
      'Gunakan penapis Pesakit saya, kemudian panggil pesakit layak seterusnya ke bilik yang tersedia.',
      'Lengkapkan konsultasi halaman penuh dan selesaikan setiap perkara senarai semak tandatangan dan tutup.',
      'Cipta surat rujukan, semak pratonton sebelah-menyebelah, kemudian tandatangan atau cetak apabila sedia.',
    ],
  },
  {
    id: 'doctor-revenue',
    title: 'Review doctor revenue and a day’s detail',
    titleBm: 'Semak hasil doktor dan butiran harian',
    minutes: 2,
    roles: ['doctor', 'locum', 'clinic_manager', 'super_admin'],
    steps: [
      'Open My revenue, or Doctor revenue when your role manages other doctors.',
      'Choose the reporting period and open a day to inspect its visit detail.',
      'Use the displayed payer, service and branch breakdowns; do not infer figures that are not returned by the API.',
    ],
    stepsBm: [
      'Buka Hasil saya, atau Hasil doktor apabila peranan anda mengurus doktor lain.',
      'Pilih tempoh laporan dan buka satu hari untuk menyemak butiran lawatan.',
      'Gunakan pecahan pembayar, perkhidmatan dan cawangan yang dipaparkan; jangan andaikan angka yang tidak dikembalikan oleh API.',
    ],
  },
  // RL-1845 (gap #142): the lab and mental-health roles, from the manual
  // chapters lab-ord, lab-res, lab-img, mh-ses and mh-acc.
  {
    id: 'lab-orders-samples',
    title: 'Take a sample for a lab order',
    minutes: 3,
    roles: ['lab'],
    titleBm: 'Ambil sampel untuk pesanan makmal',
    steps: [
      'Open Lab orders and choose your branch. An order arrives when the doctor signs; app blood-test bookings arrive the same way.',
      'Under To do, click Labels to print the tube labels, take the sample, then click Sample collected.',
      'When the sample goes to the reference lab, open the order and mark it Dispatched to lab. The patient’s app follows each step.',
    ],
    stepsBm: [
      'Buka Pesanan makmal dan pilih cawangan anda. Pesanan tiba apabila doktor menandatangani; tempahan ujian darah dalam aplikasi tiba dengan cara yang sama.',
      'Di bawah Untuk dibuat, klik Label untuk mencetak label tiub, ambil sampel, kemudian klik Sampel diambil.',
      'Apabila sampel dihantar ke makmal rujukan, buka pesanan dan tandakan Dihantar ke makmal. Aplikasi pesakit mengikut setiap langkah.',
    ],
  },
  {
    id: 'lab-results',
    title: 'Bring in lab results',
    minutes: 3,
    roles: ['lab'],
    titleBm: 'Masukkan keputusan makmal',
    steps: [
      'Open Results inbox. A message from the reference lab is matched by the MyKad (or passport) and the order number.',
      'Matched nobody · held: check the name, the last 4 digits of the MyKad and the order number, then Match it to the order, or Reject it if it is not our patient.',
      'For a paper report, click Import result, choose the order at the lab, type each result, unit, range and flag, then Save results.',
      'When every test has a result, the order goes to the ordering doctor. Only that doctor releases results to the patient.',
    ],
    stepsBm: [
      'Buka Peti keputusan. Mesej daripada makmal rujukan dipadankan melalui MyKad (atau pasport) dan nombor pesanan.',
      'Tidak padan · ditahan: semak nama, 4 digit terakhir MyKad dan nombor pesanan, kemudian Padankan dengan pesanan, atau Tolak jika bukan pesakit kita.',
      'Untuk laporan kertas, klik Import keputusan, pilih pesanan di makmal, taip setiap keputusan, unit, julat dan bendera, kemudian Simpan keputusan.',
      'Apabila setiap ujian ada keputusan, pesanan dihantar kepada doktor yang memesan. Hanya doktor itu melepaskan keputusan kepada pesakit.',
    ],
  },
  {
    id: 'lab-imaging-referrals',
    title: 'Track an X-ray or scan referral',
    minutes: 2,
    roles: ['lab'],
    titleBm: 'Jejak rujukan X-ray atau imbasan',
    steps: [
      'On Lab orders, find the referral under X-rays, scans and outside referrals and click Update.',
      'Book with the hospital or imaging centre (or ask the patient), choose the Appointment and a note, then click Appointment made.',
      'When the report arrives, paste the whole report into Report and click Attach report: it goes to the referring doctor.',
    ],
    stepsBm: [
      'Di Pesanan makmal, cari rujukan di bawah X-ray, imbasan dan rujukan luar, dan klik Kemas kini.',
      'Tempah dengan hospital atau pusat pengimejan (atau tanya pesakit), pilih Temu janji dan nota, kemudian klik Temu janji dibuat.',
      'Apabila laporan tiba, tampal seluruh laporan ke dalam Laporan dan klik Lampirkan laporan: ia dihantar kepada doktor yang merujuk.',
    ],
  },
  {
    id: 'mh-sessions',
    title: 'Follow up an app alert and book a wellbeing session',
    minutes: 3,
    roles: ['mental_health'],
    titleBm: 'Susuli amaran aplikasi dan tempah sesi kesejahteraan',
    steps: [
      'Open Sessions and choose your branch. Needs follow-up lists patients whose app answers came out as a hard time (the answers are never shown).',
      'Call the patient on the number shown. If they want a session, click Book session, choose the date, time and In person or Video, and Urgent if it should be seen first.',
      'Click Followed up on the alert. It records who followed up; nothing is sent to the patient.',
      'Your notes, plan and score stay in the restricted compartment: other staff see only that a wellbeing service took place.',
    ],
    stepsBm: [
      'Buka Sesi dan pilih cawangan anda. Perlu susulan menyenaraikan pesakit yang jawapan aplikasinya menunjukkan masa sukar (jawapan tidak pernah dipaparkan).',
      'Telefon pesakit di nombor yang dipaparkan. Jika mereka mahu sesi, klik Tempah sesi, pilih tarikh, masa dan Bersemuka atau Video, dan Segera jika perlu dilihat dahulu.',
      'Klik Telah disusuli pada amaran. Ia merekod siapa yang menyusul; tiada apa-apa dihantar kepada pesakit.',
      'Nota, pelan dan skor anda kekal dalam kompartmen terhad: kakitangan lain hanya melihat bahawa perkhidmatan kesejahteraan telah berlaku.',
    ],
  },
  {
    id: 'mh-compartment-access',
    title: 'Give or end access to wellbeing records',
    minutes: 2,
    roles: ['mental_health'],
    titleBm: 'Beri atau tamatkan akses kepada rekod kesejahteraan',
    steps: [
      'Open Compartment access. Each practitioner reads and writes only the sessions booked with them.',
      'Click Grant access, choose the person, then Summary only (the session list, no notes) or Full notes (read every session), and click Grant.',
      'Click Revoke to end an access at once. Every opening of session notes is logged.',
    ],
    stepsBm: [
      'Buka Akses kompartmen. Setiap pengamal hanya membaca dan menulis sesi yang ditempah dengan mereka.',
      'Klik Beri akses, pilih orang itu, kemudian Ringkasan sahaja (senarai sesi, tanpa nota) atau Nota penuh (baca setiap sesi), dan klik Beri.',
      'Klik Batalkan untuk menamatkan akses serta-merta. Setiap pembukaan nota sesi direkodkan.',
    ],
  },
];

export function guidesFor(role: Role): HelpGuide[] {
  return HELP_GUIDES.filter((g) => g.roles === 'all' || g.roles.includes(role));
}
